import type { SizeBounds, SliderBound } from './capacityEnvelope'

/**
 * Presentation helpers for the configure page's Size card.
 *
 * The draft keeps CPU in millicores and memory/storage in whole GB (GiB, see
 * `capacityEnvelope.GIB`); these helpers only translate that into what a
 * tenant reads and types. Nothing here changes what the SDL generator sees.
 */

export type SizeValues = { cpu: number; ram: number; storage: number }
export type SizeAxis = keyof SizeValues

export const SIZE_AXES: readonly SizeAxis[] = ['cpu', 'ram', 'storage']

/** Floor a value onto the slider's step grid, inside its bounds. */
export function clampSliderValue(value: number, bound: { min: number; max: number; step: number }): number {
  const constrained = Math.min(bound.max, Math.max(bound.min, Number.isFinite(value) ? value : bound.min))
  return bound.min + Math.floor((constrained - bound.min) / bound.step) * bound.step
}

/** Millicores as a bare core count: 500 → "0.5", 1250 → "1.25". */
export function coresNumber(milli: number): string {
  return (milli / 1000).toFixed(2).replace(/\.?0+$/, '')
}

/** Millicores with the unit tenants see in the catalog: "1 core", "4 cores". */
export function formatCores(milli: number): string {
  return `${coresNumber(milli)} ${milli === 1000 ? 'core' : 'cores'}`
}

/** A preset tile's spec, one amount per part: ["1 core", "2 GB", "20 GB"]. */
export function sizeParts(size: SizeValues): [string, string, string] {
  return [formatCores(size.cpu), `${size.ram} GB`, `${size.storage} GB`]
}

/** A preset tile's spec line: "1 core · 2 GB · 20 GB". */
export function formatSize(size: SizeValues): string {
  return sizeParts(size).join(' · ')
}

/** The summary and review line: "1 core CPU · 2 GB RAM · 20 GB storage". */
export function formatResources(size: SizeValues): string {
  return `${formatCores(size.cpu)} CPU · ${size.ram} GB RAM · ${size.storage} GB storage`
}

/**
 * A typed core count, in millicores. Accepts a decimal comma so "0,5" means
 * what a tenant on a comma locale meant. Negative, empty and non-numeric input
 * is refused rather than guessed at.
 */
export function parseCores(text: string): number | null {
  const normalized = text.trim().replace(',', '.')
  if (!/^(\d+(\.\d*)?|\.\d+)$/.test(normalized)) return null
  const milli = Math.round(Number(normalized) * 1000)
  return Number.isSafeInteger(milli) ? milli : null
}

/** A typed whole-GB amount. The draft holds integers, so "2.5" is refused. */
export function parseWholeGb(text: string): number | null {
  const normalized = text.trim()
  if (!/^\d+$/.test(normalized)) return null
  const value = Number(normalized)
  return Number.isSafeInteger(value) ? value : null
}

export function matchesPreset(draft: SizeValues, preset: SizeValues): boolean {
  return draft.cpu === preset.cpu && draft.ram === preset.ram && draft.storage === preset.storage
}

/** Axes whose request is at or above `threshold` of the bound. A zero bound has no headroom to warn about. */
export function nearLimitAxes(draft: SizeValues, bounds: SizeBounds, threshold = 0.9): SizeAxis[] {
  return SIZE_AXES.filter((axis) => {
    const bound: SliderBound = bounds[axis]
    return bound.max > 0 && draft[axis] >= bound.max * threshold
  })
}

/**
 * The Size card's choices. `whole` is the host product (everything free, priced
 * on all of it by the host); the three fixed presets and `custom` are custom
 * capacity, priced on exactly the size picked. Values are in draft units.
 */
export type SizePresetId = 'small' | 'medium' | 'large' | 'whole' | 'custom'
export type FixedPresetId = 'small' | 'medium' | 'large'

export const SIZE_PRESET_IDS: readonly SizePresetId[] = ['small', 'medium', 'large', 'whole', 'custom']
export const FIXED_PRESET_IDS: readonly FixedPresetId[] = ['small', 'medium', 'large']

/** Decision D1 (2026-10-06): 1/2/20, 2/4/50, 4/16/200 cores / GB / GB. */
export const SIZE_PRESETS: Record<FixedPresetId, SizeValues & { label: string }> = {
  small: { label: 'Small', cpu: 1000, ram: 2, storage: 20 },
  medium: { label: 'Medium', cpu: 2000, ram: 4, storage: 50 },
  large: { label: 'Large', cpu: 4000, ram: 16, storage: 200 },
}

/** Decision D2: Medium, unless a smaller preset is all that fits. */
export const SUGGESTED_PRESET: FixedPresetId = 'medium'

export function isSizePresetId(value: unknown): value is SizePresetId {
  return typeof value === 'string' && (SIZE_PRESET_IDS as readonly string[]).includes(value)
}

export function isFixedPreset(id: SizePresetId | undefined): id is FixedPresetId {
  return id !== undefined && (FIXED_PRESET_IDS as readonly string[]).includes(id)
}

/** A preset's values; `whole` is whatever the host has free, `custom` has none. */
export function presetSize(id: SizePresetId, whole: SizeValues | null = null): SizeValues | null {
  if (id === 'custom') return null
  if (id === 'whole') return whole
  const { cpu, ram, storage } = SIZE_PRESETS[id]
  return { cpu, ram, storage }
}

/** Whether a fixed preset fits the free capacity, and which axes fall short if not. */
export function presetFits(id: FixedPresetId, bounds: SizeBounds): { fits: boolean; short: SizeAxis[] } {
  const size = SIZE_PRESETS[id]
  const short = SIZE_AXES.filter((axis) => size[axis] > bounds[axis].max)
  return { fits: short.length === 0, short }
}

/** The fixed preset (or the host's whole size) these values match exactly, else `custom`. */
export function derivePreset(size: SizeValues, whole: SizeValues | null = null): SizePresetId {
  const fixed = FIXED_PRESET_IDS.find((id) => matchesPreset(size, SIZE_PRESETS[id]))
  if (fixed) return fixed
  return whole && matchesPreset(size, whole) ? 'whole' : 'custom'
}

/**
 * Where a host-bound Size card opens (decisions D2/D3). A size the tenant
 * picks needs custom capacity, which needs the host's current rates; without
 * them the whole host is all that can be rented. Never `whole` while a
 * smaller size can be priced.
 */
export function defaultPreset(bounds: SizeBounds | null, { customAvailable, wholeAvailable, suggestion = SUGGESTED_PRESET }: { customAvailable: boolean; wholeAvailable: boolean; suggestion?: FixedPresetId }): SizePresetId | null {
  if (customAvailable && bounds) {
    if (presetFits(suggestion, bounds).fits) return suggestion
    const smaller = [...FIXED_PRESET_IDS].reverse().find((id) => presetFits(id, bounds).fits)
    return smaller ?? 'custom'
  }
  return wholeAvailable ? 'whole' : null
}

/** A shortfall in the tenant's words: "Needs 4 cores; this host has 2 free". */
export function shortfallText(id: FixedPresetId, bounds: SizeBounds): string | null {
  const axis = presetFits(id, bounds).short[0]
  if (!axis) return null
  const need = SIZE_PRESETS[id][axis]
  const free = bounds[axis].max
  return axis === 'cpu'
    ? `Needs ${formatCores(need)}; this host has ${coresNumber(free)} free`
    : `Needs ${need} GB ${axis === 'ram' ? 'memory' : 'storage'}; this host has ${free} GB free`
}
